import React, { useCallback, useEffect } from "react";

import { captureException } from "@sentry/react-native";
import { StatusBar, Modal, SafeAreaView } from "react-native";
import { useTheme } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import ButtonBar from "./components/ButtonBar";
import Header from "./components/Header";
import useCropperContext, { CropperContextProvider } from "./context/cropper";
import { EditorContext } from "./context/editor";
import { EditingWindow } from "./EditingWindow";
import { Processing } from "./Processing";
import { Dimensions, EditingMode, Image } from "./types";

export type { Image as CropperImage };

type Props = {
  image: Image;
  fixedAspectRatio: number;
  minimumCropDimensions?: Dimensions;
  onEditingCancel: () => void;
  onEditingComplete: (img: Image) => Promise<void>;
  onEditingPickImage?: () => Promise<void>;
  setAspectRatio?: (aspectRatio: number | null) => void;
};

export default function ImageCropper(props: Props) {
  // skip and disable view mode if modifying the aspect ratio is disabled
  // as the user must crop the image for it to be valid.
  const initialEditingMode: EditingMode = props.setAspectRatio
    ? "view"
    : "crop";

  const tailwind = useTailwind();

  return (
    <Modal style={tailwind("flex-1 relative")} animationType="slide">
      <CropperContextProvider initialEditingMode={initialEditingMode}>
        <ImageCropperCore {...props} />
      </CropperContextProvider>
    </Modal>
  );
}

function ImageCropperCore(props: Props) {
  const {
    minimumCropDimensions = { width: 100, height: 100 },
    fixedAspectRatio,
    onEditingCancel,
    onEditingComplete,
    image,
    setAspectRatio,
    onEditingPickImage,
  } = props;

  const {
    imageData,
    setImageData,
    setIsReady,
    setEditingMode,
    initialEditingMode,
    setIsProcessing,
    editingMode,
  } = useCropperContext();

  const init = useCallback(async () => {
    setImageData(image);

    setIsReady(true);
  }, [image, setImageData, setIsReady]);

  useEffect(() => {
    init().catch(captureException);
  }, [init]);

  async function onBackPress() {
    setIsProcessing(true);

    switch (editingMode) {
      case "view":
        onEditingCancel();
        break;
      case "crop":
        if (initialEditingMode === "view") {
          setEditingMode("view");
        } else {
          onEditingCancel();
        }
        break;
      case "operation-select": {
        await init();
        setEditingMode("crop");
        break;
      }
    }

    setIsProcessing(false);
  }

  const onSave = async () => {
    setIsProcessing(true);
    await onEditingComplete(imageData);
    setIsProcessing(false);
  };

  const onPickImage = onEditingPickImage
    ? async () => {
        setTimeout(() => setIsProcessing(true), 1000);
        await onEditingPickImage();
        setIsProcessing(false);
      }
    : undefined;

  return (
    <EditorContext.Provider
      value={{
        minimumCropDimensions,
        fixedAspectRatio,
        onBackPress,
        onSave,
        setAspectRatio,
        onPickImage,
      }}
    >
      <StatusBar hidden />
      <ImageEditorView />
    </EditorContext.Provider>
  );
}

export function ImageEditorView() {
  const { isReady, isProcessing } = useCropperContext();

  const { colors } = useTheme();
  const tailwind = useTailwind();

  return (
    <>
      {isReady && (
        <SafeAreaView
          style={[tailwind("flex-1"), { backgroundColor: colors.surface }]}
        >
          <Header />
          <EditingWindow />
          <ButtonBar />
        </SafeAreaView>
      )}
      {isProcessing && <Processing />}
    </>
  );
}
